import { useCallback, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { useCreateDocument } from "../../../../../hooks/useCreateDocument";
import { documentTypeFromExtension } from "../utils/documentTypeFromExtension";

interface DocumentUploadZoneProps {
  projectId: string;
  onComplete: () => void;
}

export default function DocumentUploadZone({
  projectId,
  onComplete,
}: DocumentUploadZoneProps) {
  const { createDocument, loading, error } = useCreateDocument(projectId);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;
      setUploadError(null);
      setProgress({ current: 0, total: fileArray.length });

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        setProgress({ current: i + 1, total: fileArray.length });
        try {
          const documentType = documentTypeFromExtension(file.name);
          await createDocument({
            projectId,
            attachment: file,
            documentType,
          });
        } catch (err) {
          setUploadError(
            err instanceof Error ? err.message : "Falha ao enviar arquivo."
          );
          break;
        }
      }

      setProgress(null);
      onComplete();
    },
    [projectId, createDocument, onComplete]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = e.dataTransfer.files;
      if (files?.length) processFiles(files);
    },
    [processFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files?.length) processFiles(files);
      e.target.value = "";
    },
    [processFiles]
  );

  return (
    <Card>
      <CardContent className="p-8">
        <h3 className="text-lg font-light tracking-tight text-primary-950 dark:text-white mb-6">
          ENVIAR ARQUIVOS
        </h3>

        {(error?.message || uploadError) && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>
              {uploadError ?? error?.message}
            </AlertDescription>
          </Alert>
        )}

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed p-12 text-center transition-colors duration-300
            ${
              dragOver
                ? "border-primary-950 dark:border-white bg-primary-50 dark:bg-primary-800"
                : "border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600"
            }
          `}
        >
          <input
            type="file"
            multiple
            onChange={handleFileInputChange}
            disabled={loading}
            className="hidden"
            id="document-upload-input"
          />
          <label
            htmlFor="document-upload-input"
            className="cursor-pointer block text-primary-600 dark:text-primary-400 text-sm uppercase tracking-widest"
          >
            {progress
              ? `Enviando ${progress.current} de ${progress.total}...`
              : loading
                ? "Enviando..."
                : "Arraste arquivos aqui ou clique para selecionar"}
          </label>
        </div>
      </CardContent>
    </Card>
  );
}
